// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Zustand und Abfrage des Overlays.
//
// Sitzt in App.tsx UNTER ThemeProvider und ÜBER ThemeSettings: der Schalter liegt im
// Einstellungs-Drawer, und der hängt in ThemeSettings. Der Provider braucht seinerseits den
// Router (Routen-Muster), den SettingsContext (Menüführung) und den Apollo-Client — alle drei
// stehen an dieser Stelle bereits.
//
// CONTRACT: Der Zustand ist ein SITZUNGSZUSTAND und wird NIRGENDS gespeichert — kein
// localStorage, kein SettingsContext. Ein gespeichertes „an“ hieße, dass die nächste Anmeldung
// am selben Rechner mit einer Wolke über der Maske beginnt, womöglich bei einer anderen Person.
// Deshalb ist der Schalter auch vom „Zurücksetzen“ des Drawers ausgenommen (HeatmapOptions.tsx).
//
// CONTRACT: Das Overlay geht beim Abmelden und beim Rollenverlust aus, und ein Nicht-SUPERADMIN
// kann es nie einschalten. Das Gate ist KOSMETISCH — verbindlich sind usageAccess.ts und die
// Whitelist im Backend. Es steht hier trotzdem, weil eine laufende Abfrage nach dem
// Rollenverlust in Forbidden liefe und die Oberfläche eine Karte zeigte, die niemand mehr
// sehen darf.
//
// EINE Abfrage für beide Verbraucher: Zeichenfläche und Leiste lesen dasselbe Ergebnis aus dem
// Context. Zwei Hooks ergäben zwei Anfragen je Seitenwechsel und damit zwei Audit-Zeilen.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import UserContext from '../../../context/UserProvider/UserContext';
import { getCurrentUser } from '../../../context/CommunicationProvider/authUser';
import { useSettingsContext } from '../../../components/settings/SettingsContext';
import { istSuperAdmin } from '../../../utils/istSuperAdmin';
import { toRoutePattern, UNKNOWN_ROUTE } from '../services/routePattern';
import type { UsageClickMap, UsageDays } from '../types';
import { HeatmapContext, HeatmapZustand, HEATMAP_STANDARD_ZEITRAUM } from './HeatmapContext';
import HeatmapLeiste from './HeatmapLeiste';
import HeatmapOverlay from './HeatmapOverlay';
import { useUsageClickMap } from './useUsageClickMap';
import { breiteDerKlasse, eigeneKlasse } from './viewportClasses';

interface Props {
  children: React.ReactNode;
}

/** Gewählte Klasse samt der Breite, mit der sie abgefragt wird. */
interface Klassenwahl {
  label: string;
  width: number;
  /** Ist das (noch) die Klasse des eigenen Fensters? Nur dann wird sie nachgeführt. */
  eigene: boolean;
}

/** Fensterbreite im vom Server zugelassenen Bereich (assertViewportWidth: 1..65535). */
const gueltigeBreite = (roh: number): number => Math.max(1, Math.min(65535, Math.round(roh) || 1));

/** Die Wahl „eigenes Fenster" — Label und echte Breite, beide aus demselben Messpunkt. */
const eigeneWahl = (): Klassenwahl => {
  const breite = window.innerWidth;
  return { label: eigeneKlasse(breite), width: gueltigeBreite(breite), eigene: true };
};

/**
 * Wartezeit, bevor eine geänderte Fensterbreite übernommen wird. Beim Ziehen des Fensterrands
 * feuert `resize` pixelweise; jede neue Breite wäre sonst eine eigene Abfrage samt Audit-Zeile.
 */
const RESIZE_RUHE_MS = 250;

/**
 * Die Karte, die gezeichnet werden darf — oder null. Exportiert, damit die Regel ohne
 * Provider-Baum prüfbar ist; die Provider-Tests belegen sie zusätzlich im Zusammenspiel.
 */
export const karteFuer = (
  route: string,
  lage: {
    ohneKarte: boolean;
    enabled: boolean;
    loading: boolean;
    /** Fehler der Abfrage: Apollo lässt die letzten Daten daneben stehen (errorPolicy 'none'). */
    error: unknown;
    antwort: UsageClickMap | null | undefined;
  },
): UsageClickMap | null => {
  // Vier Verwerfungsgründe, jeder mit eigenem Beleg: kein Muster, aus, Antwort unterwegs, Fehler
  // (KI-Review !746: sonst zeichnete die Fläche die Karte der alten Parameter, während die Leiste
  // den Fehler meldet — dieselbe Regel wie `!insights.error` auf der Nutzungsseite).
  if (lage.ohneKarte || !lage.enabled || lage.loading || lage.error) return null;
  const antwort = lage.antwort ?? null;
  if (!antwort || antwort.route !== route) return null;
  return antwort;
};

export function HeatmapProvider({ children }: Props) {
  const user = useContext(UserContext);
  const settings = useSettingsContext();
  const location = useLocation();

  // CONTRACT: BEIDE Rollenquellen müssen die Rolle tragen, und beide tragen aus einem eigenen
  // Grund:
  //  - der TOKEN, weil der Server mit genau ihm autorisiert (usageAccess.ts). Ein Schalter, der
  //    einer neueren Profilantwort glaubt, böte Bedienung an, die der Server ablehnt — und ein
  //    Rollenwechsel in der laufenden Anwendung wirkt hier sofort.
  //  - der NUTZERKONTEXT, weil nur er sich ändert, wenn das Profil die Rolle entzieht: ein Gate
  //    allein am Token beobachtet nichts und schlüge erst zu, wenn irgendetwas anderes ein
  //    Rendern auslöst. Brief 1 verlangt ein Gate, das von selbst fällt.
  // Gehen beide auseinander, gewinnt fail-closed: das Gate ist kosmetisch, verbindlich bleiben
  // usageAccess.ts und die Whitelist im Backend.
  const verfuegbar = Number(user?.id) > 0 && istSuperAdmin(getCurrentUser()) && istSuperAdmin(user);

  const [gewuenscht, setGewuenscht] = useState(false);
  const [days, setDaysState] = useState<UsageDays>(HEATMAP_STANDARD_ZEITRAUM);
  // Die eigene Klasse wird MIT ihrer echten Breite gemerkt: der Server klassifiziert selbst, und
  // die echte Breite ist die genauere Angabe. Eine andere Klasse schickt die repräsentative
  // Breite aus viewportClasses.ts — die Karte gilt dann für diese Klasse, nicht für das eigene
  // Fenster (nur so lässt sich anschauen, wie eine Seite auf breiteren Schirmen benutzt wird).
  const [klasse, setKlasse] = useState<Klassenwahl>(eigeneWahl);
  // Die Klasse des eigenen Fensters getrennt gehalten: die Auswahl macht sie kenntlich, auch
  // während eine ANDERE Klasse angesehen wird.
  const [eigenesKlassenLabel, setEigenesKlassenLabel] = useState<string>(() => eigeneKlasse(window.innerWidth));

  // CONTRACT: Die eigene Klasse ist keine Momentaufnahme des Seitenaufrufs. Der Provider hängt
  // über dem gesamten Baum und wird je Dokumentladung genau einmal eingehängt; ohne Nachführung
  // zeigte er nach jedem Maximieren des Fensters die Wolke der ALTEN Klasse, gezeichnet auf der
  // NEUEN Breite (die Zeichenfläche rechnet mit dem aktuellen innerWidth). Eine bewusst gewählte
  // andere Klasse bleibt dagegen stehen — sie ist der Zweck der Auswahl.
  useEffect(() => {
    let wartend: ReturnType<typeof setTimeout> | null = null;
    const nachfuehren = () => {
      wartend = null;
      const jetzt = eigeneWahl();
      setEigenesKlassenLabel(jetzt.label);
      setKlasse((bisher) => {
        if (!bisher.eigene) return bisher;
        return bisher.label === jetzt.label && bisher.width === jetzt.width ? bisher : jetzt;
      });
    };
    const angestossen = () => {
      if (wartend !== null) clearTimeout(wartend);
      wartend = setTimeout(nachfuehren, RESIZE_RUHE_MS);
    };
    window.addEventListener('resize', angestossen);
    return () => {
      if (wartend !== null) clearTimeout(wartend);
      window.removeEventListener('resize', angestossen);
    };
  }, []);

  // Zwei Sicherungen, und beide tragen: der Effekt VERGISST den Wunsch (nach einem Rollenverlust
  // geht das Overlay nicht von selbst wieder an, wenn die Rolle zurückkommt), die Ableitung
  // schließt das eine Render-Fenster dazwischen, in dem sonst noch abgefragt würde.
  useEffect(() => {
    if (!verfuegbar) setGewuenscht(false);
  }, [verfuegbar]);
  const enabled = gewuenscht && verfuegbar;

  const setEnabled = useCallback(
    (next: boolean) => {
      setGewuenscht(next && verfuegbar);
    },
    [verfuegbar],
  );

  const setDays = useCallback((tage: UsageDays) => setDaysState(tage), []);

  // Eine Wahl aus der Liste ist immer eine BEWUSSTE Wahl — auch wenn sie zufällig auf die eigene
  // Klasse fällt. Sie wird deshalb nicht mehr nachgeführt; wer zurück zum eigenen Fenster will,
  // wählt es erneut. Stillschweigend die Auswahl beim Ziehen des Fensterrands zu ändern wäre die
  // schlechtere Überraschung.
  const setViewportClassLabel = useCallback((label: string) => {
    setKlasse({ label, width: breiteDerKlasse(label), eigene: false });
  }, []);

  const route = toRoutePattern(location.pathname);
  // Ohne Routen-Muster gibt es keine Karte (die Lese-API lehnt 'unbekannt' ab); der Hook
  // überspringt dann. Die Regel steht hier NOCH EINMAL, damit die Karte nicht am Innenleben
  // des Hooks hängt (KI-Review !746: bei `skip` liefert Apollo 3.8 `data: undefined` — ein
  // späteres Apollo könnte alte Daten stehen lassen).
  const ohneKarte = route === UNKNOWN_ROUTE || route === '';

  const abfrage = useUsageClickMap({
    enabled,
    route,
    viewportWidth: klasse.width,
    themeLayout: String(settings.themeLayout),
    themeStretch: Boolean(settings.themeStretch),
    days,
  });

  const { refetch } = abfrage;
  const erneutVersuchen = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  const wert = useMemo<HeatmapZustand>(
    () => ({
      verfuegbar,
      enabled,
      setEnabled,
      days,
      setDays,
      viewportClassLabel: klasse.label,
      setViewportClassLabel,
      eigenesKlassenLabel,
      route,
      // Bei Routen-, Klassen- oder Zeitraumwechsel darf die ALTE Karte nicht stehen bleiben:
      // Apollo hält sie wegen notifyOnNetworkStatusChange bis zur neuen Antwort, und die Wolke
      // der vorigen Seite über der neuen Maske wäre schlicht falsch. Verwerfungsgründe (karteFuer):
      // Antwort unterwegs, Abfrage übersprungen (aus oder ohne Muster), Fehler der Abfrage oder
      // eine Antwort, die laut ihrer eigenen `route` zu einer anderen Seite gehört.
      karte: karteFuer(route, {
        ohneKarte,
        enabled,
        loading: abfrage.loading,
        error: abfrage.error,
        antwort: abfrage.data?.usageClickMap,
      }),
      loading: abfrage.loading,
      error: abfrage.error,
      erneutVersuchen,
    }),
    [
      verfuegbar,
      enabled,
      setEnabled,
      days,
      setDays,
      klasse.label,
      setViewportClassLabel,
      eigenesKlassenLabel,
      route,
      abfrage.loading,
      abfrage.data,
      abfrage.error,
      erneutVersuchen,
    ],
  );

  // Einhängepunkt: nur der Provider weiß, ob das Overlay läuft. Beide Teile portalieren sich
  // selbst nach document.body; ausgeschaltet liegt NICHTS im Dokument — eine dauerhaft
  // vorhandene, unsichtbare Schicht wäre eine Fehlerquelle, die niemand sieht.
  return (
    <HeatmapContext.Provider value={wert}>
      {children}
      {enabled ? <HeatmapOverlay /> : null}
      {enabled ? <HeatmapLeiste /> : null}
    </HeatmapContext.Provider>
  );
}

export default HeatmapProvider;
